import type { AccountSlugRefusal } from 'fym-shared/src/music24/accountSlug';
import type { SerializedImageSourceFile } from 'fym-shared/src/music24/imageSourceFile';
import type { StreamingUgcPlatform } from 'fym-shared/src/music24/streamingSections';
import { SmartLinkStatsResponse, SmartLinkStatsSummariesResponse } from 'fym-shared/src/smartlinks/stats';
import type { Music24PlanTier } from 'fym-shared/src/music24/music24Plans';
import type { CleanCopiesAnswer } from 'fym-shared/src/music24/watermarkUpsell';
import type { CoverDoc } from 'fym-shared/src/music24/coverDoc';
import type { CoverRedoRequest } from 'fym-shared/src/music24/coverRedo';
import type { DateRedoRequest } from 'fym-shared/src/music24/dateRedo';
import type { AssetSpecId } from 'fym-shared/src/music24/assetSpecs';
import type { KitAssetSource, KitAssetYouTube, SoundCheck } from 'fym-shared/src/music24/addedAssets';
import type { SongMusicVideoSummary } from 'fym-shared/src/music24/musicVideoApi';
import type { KitShape, StudioMoment, StudioThemeId } from 'fym-shared/src/music24/studioThemes';
import type { KitFontId, ReleaseKitLook, ReleaseThemeId, ThemeColours } from 'fym-shared/src/music24/releaseThemes';
import type { AssetStyleId, WaveformShape } from 'fym-shared/src/music24/assetStyles';
import type { KitAssetTrim } from 'fym-shared/src/music24/fullVideoPlan';
import type { SpotifyHeaderSource } from 'fym-shared/src/music24/spotifyHeader';
import type {
  ArtistProfileBio,
  ProfileMakeItem,
  ProfileManualService,
  SerializedProfileAsset,
  SpotifyProfileImage,
} from 'fym-shared/src/music24/artistProfile';
import type { ProfileSendTarget } from 'fym-shared/src/music24/profileSend';
import type {
  AssetShareAccess,
  AssetShareAccessResponse,
  AssetShareKind,
  AssetShareResponse,
  PublicAssetShare,
  SerializedAssetShare,
} from 'fym-shared/src/music24/assetShares';
import type { LyricSyncState, LyricTimingSummary, LyricTimingV1 } from 'fym-shared/src/music24/lyricTiming';
import type { ReelsPlanSettings } from 'fym-shared/src/music24/reelsPlan';
import type { RolloutDayKey, RolloutKind } from 'fym-shared/src/music24/releaseRollout';
import { SmartLinkThemeUsageResponse } from 'fym-shared/src/smartlinks/types';
import type {
  BioLinkAutoAddResult,
  BioLinkSettings,
  BioLinkSettingsResponse,
} from 'fym-shared/src/smartlinks/bioLinkSettings';
import type { WizardPublishResult } from 'fym-shared/src/smartlinks/wizardPublish';
import { PresaveCampaignStatus } from 'fym-shared/src/smartlinks/presave';
import {
  ArtistScrapeStatus,
  CatalogItemKind,
  CatalogItemOwnership,
  CatalogSuggestionMatchConfidence,
  CatalogSuggestionSource,
  ServiceConnectionStatus,
} from 'fym-shared/src/smartlinks/catalog';
import {
  ArtistPortalArtistRef,
  ArtistPortalArtistsResponse,
  ArtistPortalAuthBeginResponse,
  ArtistPortalAuthCompleteBody,
  ArtistPortalConnectionFields,
  ArtistPortalEarningsGroup,
  ArtistPortalOverviewResponse,
  ArtistPortalPlaylistsResponse,
  ArtistPortalTrackDailyResponse,
  ArtistPortalTracksResponse,
} from 'fym-shared/src/smartlinks/artistPortals';
import { ValueOf } from 'fym-common/src/types/utilityTypes';
import { RawWidgetData, SmartLinkWidgetType } from 'fym-common/src/db/enums';
import {
  SerializedAccount,
  SerializedAccountTracker,
  SerializedSmartLink,
  SerializedSmartLinkVersionTheme,
  SerializedUser,
  SerializedWidget,
} from 'fym-common/src/db/serializersTypes';
import { Music24LicenseStateType } from 'fym-common/src/license/music24Types';
import type { SerializedReel, SocialPlatform } from '../../modules/music24/promote/types';

// -------------------------------- RESULTS --------------------------------

export type GetUserResult = {
  /**
   * `music24Locale`: the UI language the user saved (one of `MUSIC24_LOCALES`), null for
   * "Automatic". An older server sends no field.
   */
  /**
   * `firstName`: the first name from the newest Apple or Google sign-in with a name, null for an
   * email sign-in. An older server sends no field. The signed-in hint for music24.com uses it.
   */
  user: SerializedUser & { music24Locale?: string | null; firstName?: string | null };
  license: Music24LicenseStateType;
  token: string;
};

export type VerifySlugResult = {
  isValid: boolean;
  isAvailable: boolean;
  availableSlug: string;
  /** A reserved address (an API since 2026-10-28 also sends `reason: 'reserved'`). */
  isReserved?: boolean;
  /** Why the address cannot be used, or null; absent on an older API (QA brk-r2-4). */
  reason?: AccountSlugRefusal | 'taken' | null;
};
export type VerifyEmailResult = {
  isAvailable: boolean;
};

export type GetTrackersResponse = {
  trackers: SerializedAccountTracker[];
};

export type TrackerResponse = {
  tracker: SerializedAccountTracker;
};

// -------------------------------- RESPONSES --------------------------------

export type AccountResponse = {
  /**
   * `masterRightsConfirmed` is true after a member of the account ticked the rights box on a
   * master upload (design g1). Optional: an older API leaves it out, and the app then asks.
   */
  account: SerializedAccount & { masterRightsConfirmed?: boolean };
};

export type SmartLinkResponse = {
  smartLink: SerializedSmartLink;
};

export interface WidgetResponse extends SmartLinkResponse {
  widget: SerializedWidget;
}

export type RemoveAccountDataResponse = void;

export type FetchSmartLinkStatsResponse = SmartLinkStatsResponse;

export type FetchAccountStatsResponse = SmartLinkStatsResponse;

// -------------------------------- OPTIONS --------------------------------
export type GetAccountOptions = {
  accountId: number;
};

export type CreateAccountOptions = {
  name: string;
  slug: string;
};

export type UpdateAccountOptions = {
  id: number;
  name?: string;
  slug?: string;
  avatarFileId?: number;
  /**
   * Which of the platform hosts this account's pages are advertised on. Null
   * resets it to the default; left out, it is not touched.
   */
  platformDomain?: string | null;
};

export type PostImageOptions = {
  accountId: number;
  formData: FormData;
};

export type VerifyAccountNameSlugOptions = {
  slug: string;
  accountId?: number;
};

export type VerifyAccountEmailOptions = {
  email: string;
};

export type GetSmartLinksOptions = {
  accountId: number;
  page: number;
  limit?: number;
  search?: string;
  /** The tab of the SmartLinks page. Left out, every page. */
  status?: 'live' | 'draft';
};

export type GetSmartLinkOptions = {
  accountId: number;
  smartLinkId: number;
};

export type UpdateSmartLinkOptions = {
  accountId: number;
  smartLinkId: number;
  name?: string;
  slug: string;
  theme?: SerializedSmartLinkVersionTheme;
  backgroundFileId?: number;
  /**
   * Which of the platform hosts the page is advertised on. Null resets it to
   * the account's choice; left out, it is not touched.
   */
  platformDomain?: string | null;
};

export type PublishSmartLinkOptions = {
  accountId: number;
  smartLinkId: number;
};

/** Taking a page offline. It becomes a draft again — nothing it holds is lost. */
export type UnpublishSmartLinkOptions = {
  accountId: number;
  smartLinkId: number;
};

export type CreateSmartLinkOptions = {
  accountId: number;
  name: string;
  slug: string;
  theme: SerializedSmartLinkVersionTheme;
  backgroundFileId?: number;
  /**
   * Which of the platform hosts the page is advertised on. Left out, or null,
   * it follows the account's choice.
   */
  platformDomain?: string | null;
};

export type DeleteSmartLinkOptions = {
  accountId: number;
  smartLinkId: number;
};

export type VerifySmartLinkSlugOptions = {
  slug: string;
  accountId: number;
  smartLinkId?: number;
};

export type UpdateWidgetOptions = {
  accountId: number;
  smartLinkId: number;
  widgetUUID: string;
  position: number;
  type: SmartLinkWidgetType;
  data?: ValueOf<RawWidgetData>;
  /** The eye of the editor: true hides the block on the page. Left out, the flag does not change. */
  hidden?: boolean;
};

export type CreateWidgetOptions = {
  accountId: number;
  smartLinkId: number;
  type: SmartLinkWidgetType;
  data: ValueOf<RawWidgetData>;
  /** "+ Add here": the place of the new block (the blocks at and after it move down). Left out: the end. */
  position?: number;
  /** Undo of a delete puts a hidden block back hidden. Left out: shown. */
  hidden?: boolean;
};

export type DeleteWidgetOptions = {
  accountId: number;
  smartLinkId: number;
  widgetUUID: string;
};

export type CreateWizardReleaseLinkOptions = {
  accountId: number;
  title?: string;
  subtitle?: string;
  extra?: string;
  imageId?: number;
  imageURL?: string;
  urls?: string[];
  /** musicapi `maid` of the catalog track the release link was built from. */
  catalogTrackMaid?: string;
  /** The address the creation sheet asked for. Left out, the title makes one. */
  slug?: string;
  /** The theme the creation sheet asked for. Left out, the page is `dark`. */
  theme?: SerializedSmartLinkVersionTheme;
  /**
   * Which of the platform hosts the page is advertised on. Null resets it to
   * the account's choice; left out, it is not touched.
   */
  platformDomain?: string | null;
  /** The add-song flow publishes the page at once (owner decision 6, fym-shared `smartlinks/wizardPublish.ts`). */
  publish?: boolean;
};

/** A wizard answer. `publishResult` is absent on an older API and when the request did not ask to publish. */
export type WizardSmartLinkResponse = SmartLinkResponse & { publishResult?: WizardPublishResult };

export type CreateWizardPlaylistLinkOptions = {
  accountId: number;
  url: string;
};

export type DuplicateSmartLinkOptions = {
  accountId: number;
  smartLinkId: number;
  name: string;
  theme: SerializedSmartLinkVersionTheme;
  slug: string;

  backgroundFileId?: number;
};

export type RemoveAccountDataOptions = void;
export type GetUserOptions = {
  userId?: number;
};
export type FetchSmartLinkStatsOptions = {
  accountId: number;
  smartLinkId: number;
  /** Left out, the server uses 14 days. The server cuts the days to the plan history. */
  days?: SmartLinkStatsDays;
};

/**
 * The windows the SmartLink stats routes take with `?days=`. The server cuts them to the plan
 * history and answers `limited` (`SMART_LINK_STATS_DAYS_OPTIONS` in fym-shared).
 */
export type SmartLinkStatsDays = 7 | 14 | 28 | 90 | 180 | 365;

export type FetchAccountStatsOptions = {
  accountId: number;
  /** Left out, the server uses 14 days. */
  days?: SmartLinkStatsDays;
};

export type FetchSmartLinkStatsSummariesResponse = SmartLinkStatsSummariesResponse;

export type FetchSmartLinkStatsSummariesOptions = {
  accountId: number;
  /** Left out, the server uses 14 days. */
  days?: SmartLinkStatsDays;
};

export type FetchSmartLinkThemesResponse = SmartLinkThemeUsageResponse;

export type FetchSmartLinkThemesOptions = {
  accountId: number;
};

export type CreateTrackerOptions = {
  accountId: number;
  snippet: string;
};

export type UpdateTrackerOptions = {
  accountId: number;
  trackerId: number;
  snippet: string;
};

export type DeleteTrackerOptions = {
  accountId: number;
  trackerId: number;
};

// -------------------------------- PRE-SAVE --------------------------------

export type CreateWizardPresaveLinkOptions = {
  accountId: number;
  title: string;
  artistName: string;
  /** ISO 8601 date, today or later. */
  releaseDate: string;
  /**
   * A file we already hold — a catalog row's uploaded cover. Sent instead of
   * `imageURL`: a relative file url is nothing the server could download.
   */
  imageId?: number;
  imageURL?: string;
  /** musicapi `maid` of the catalog track the campaign was built from. */
  catalogTrackMaid?: string;
  /**
   * The unreleased catalog row the wizard was opened from. It ties the campaign
   * to that row by key, so the Catalog screen stops guessing from the title.
   */
  catalogItemUuid?: string;
  /** MusicAPIIntegrationID values. Left out to get the server's default five. */
  services?: string[];
  /** The address the creation sheet asked for. Left out, the title makes one. */
  slug?: string;
  /** The theme the creation sheet asked for. Left out, the page is `dark`. */
  theme?: SerializedSmartLinkVersionTheme;
  /**
   * Which of the platform hosts the page is advertised on. Null resets it to
   * the account's choice; left out, it is not touched.
   */
  platformDomain?: string | null;
  /** The add-song flow publishes the page at once (owner decision 6, fym-shared `smartlinks/wizardPublish.ts`). */
  publish?: boolean;
};

export type PresaveCampaign = {
  id: number;
  uuid: string;
  accountId: number;
  smartLinkId: number;
  status: PresaveCampaignStatus;
  releaseDate: string;
  trackTitle: string;
  artistName: string;
  /** service → url, filled in when the campaign is released. */
  resolvedUrls: Record<string, string> | null;
  catalogTrackMaid: string | null;
  artistRemindedAt: string | null;
  createdAt: string;
};

export type PresaveCampaignStats = {
  /** `today` is the same day's share of `count`, both in UTC. */
  byService: { integrationType: string; count: number; today: number }[];
  /** Service sign-ups from today, the sum of `byService[].today`. */
  todayCount: number;
  emailCount: number;
  emailTodayCount: number;
  /** Fans who also asked for the artist's news. */
  optInNewsCount: number;
  /** Seven days of email sign-ups, oldest first, zeroes included. */
  emailDailySeries: { date: string; count: number }[];
  executedCount: number;
  emailedCount: number;
  needsReauthCount: number;
  failedCount: number;
  /** The artist subscribers who get the next release (Pre-save Subscriptions). Absent in an older answer. */
  artistSubscriberCount?: number;
};

export type GetPresaveCampaignOptions = {
  accountId: number;
  /** The numeric id or the campaign uuid the pre-save widget carries. */
  campaignId: number | string;
};

export type GetPresaveCampaignResponse = {
  campaign: PresaveCampaign;
  stats: PresaveCampaignStats;
};

export type ReleasePresaveCampaignOptions = {
  accountId: number;
  campaignId: number;
  /** The campaign's uuid, so the cache entry the panel reads is invalidated too. */
  campaignUuid?: string;
  /** The page the release rewrites, so its cache entry is invalidated too. */
  smartLinkId?: number;
  urls: string[];
};

export type ReleasePresaveCampaignResponse = {
  campaign: Pick<PresaveCampaign, 'id' | 'status'> & { resolvedUrls: Record<string, string> };
  smartLink: SerializedSmartLink;
};

export type CancelPresaveCampaignOptions = {
  accountId: number;
  campaignId: number;
  /** The campaign's uuid, so the cache entry the panel reads is invalidated too. */
  campaignUuid?: string;
  /** The page the block belongs to, so its cache entry is invalidated too. */
  smartLinkId?: number;
};

export type CancelPresaveCampaignResponse = {
  campaign: PresaveCampaign;
};

export type CreatePresaveCampaignForSmartLinkOptions = {
  accountId: number;
  smartLinkId: number;
  title: string;
  artistName: string;
  /** ISO 8601 date, today or later. */
  releaseDate: string;
};

/**
 * Only the campaign. The block that draws it is created right after with the
 * normal widget POST, carrying the uuid this call returns.
 */
export type CreatePresaveCampaignForSmartLinkResponse = {
  campaign: PresaveCampaign;
};

export type UpdatePresaveCampaignOptions = {
  accountId: number;
  campaignId: number;
  /** The campaign's uuid, so the cache entry the block editor reads is invalidated too. */
  campaignUuid?: string;
  /** The page that draws the campaign, so its cache entry is invalidated too. */
  smartLinkId?: number;
  title?: string;
  artistName?: string;
  releaseDate?: string;
};

export type UpdatePresaveCampaignResponse = {
  campaign: PresaveCampaign;
};

// -------------------------------- CATALOG --------------------------------

/**
 * Which slice of the catalog the table shows. The wire values are the server's
 * `CatalogFilter`; `all` is the renderer's own "send no filter at all".
 */
export enum CatalogFilterValue {
  all = 'all',
  claimed = 'claimed',
  unreleased = 'unreleased',
  noLink = 'no-link',
}

/**
 * Our own copy of the cover of a row. `url` is relative, as everywhere, and streams the original:
 * use it only for a download. `sourceUrl` is the imgproxy source of the picture (an API from
 * before 2026-10-19 does not send it); draw the cover with `catalogArtworkSrc`.
 */
export type CatalogItemArtworkFile = SerializedImageSourceFile;

export type CatalogItem = {
  uuid: string;
  kind: CatalogItemKind;
  ownership: CatalogItemOwnership;
  catalogTrackMaid: string | null;
  title: string;
  artistName: string;
  releaseType: string | null;
  /** ISO 8601, or null while the artist has not fixed the day. */
  releaseDate: string | null;
  /** The snapshot url taken at claim time. Null on unreleased rows. */
  artworkUrl: string | null;
  /** The uploaded artwork, for unreleased rows. Null on claimed ones. */
  artworkFile: CatalogItemArtworkFile | null;
  /** MusicAPIIntegrationID → url, snapshotted at claim time. */
  serviceLinks: Record<string, string> | null;
  claimedAt: string;
  smartLinkId: number | null;
  /**
   * Every SmartLink of the song: each page of the same catalog track and the pre-save page.
   * `smartLinkId` is the first of them. Absent on an older answer.
   */
  smartLinkIds?: number[];
  smartLinkSlug: string | null;
  smartLinkStatus: 'published' | 'draft' | null;
  presaveCampaignUuid: string | null;
  /** The song files of the row: the master, its state and the lyrics (design f1). */
  song: CatalogSongSummary;
  /** The current music video of the song (spec 2026-10-09 §3.2); null: none. Older answers have no field. */
  musicVideo?: SongMusicVideoSummary | null;
};

/** `processing` until the AAC copy and the analysis are there; `failed` after the last attempt. */
export type CatalogMasterStatus = 'processing' | 'ready' | 'failed';

/**
 * Why a failed master failed: the file does not decode, or is too short or too long. Null (or
 * absent, from an older API) when the reason is not known.
 */
export type CatalogMasterFailReason = 'decode' | 'tooShort' | 'tooLong';

/** The song state on every catalog row (music24-api `SerializedCatalogSongSummary`). */
export type CatalogSongSummary = {
  hasMaster: boolean;
  masterStatus: CatalogMasterStatus | null;
  masterFailReason?: CatalogMasterFailReason | null;
  /** True when the AAC copy exists and the master did not fail. Old answers have no field. */
  canPlay?: boolean;
  hasLyrics: boolean;
  /** The lyric sync state (spec 2026-10-05 §9.1). Old answers have no field. */
  lyricSyncStatus?: LyricSyncState;
  durationSec: number | null;
  bpm: number | null;
};

/** The song files of one row, for the song files sheet (music24-api `SerializedCatalogSong`). */
export type CatalogSong = {
  master: {
    fileUuid: string;
    name: string;
    sizeBytes: number;
    status: CatalogMasterStatus;
    failReason?: CatalogMasterFailReason | null;
    /** True when the AAC copy exists and the master did not fail. Old answers have no field. */
    canPlay?: boolean;
    durationSec: number | null;
    bpm: number | null;
    hookStartSec: number | null;
    /** When the master was saved (ISO 8601). Old answers have no field. */
    uploadedAt?: string | null;
    /** `detected`, or `fallback` (a fixed 120 BPM grid). Null with no analysis. Old answers have no field. */
    beatGrid?: 'detected' | 'fallback' | null;
  } | null;
  /** What Music24 keeps from the master (the song page, design song-page c1). Old answers have no field. */
  kept?: { playCopy: boolean; vocalStem: boolean; analysis: boolean };
  lyrics: string | null;
  hasLyricTiming: boolean;
  /** The lyric timing in short; null when the row was never synced. The lines are in `getLyricTiming`. */
  lyricTiming?: LyricTimingSummary | null;
  isrc: string | null;
  /** The UPC / EAN of the release, 12 to 14 digits. Old answers have no field. */
  upc?: string | null;
  made: { reels: number; clips: number; posts: number; kitAssets: number };
  /** The current music video of the song (spec 2026-10-09 §3.2); null: none. Older answers have no field. */
  musicVideo?: SongMusicVideoSummary | null;
};

export type CatalogSongResponse = {
  song: CatalogSong;
};

export type GetCatalogSongOptions = {
  accountId: number;
  itemUuid: string;
};

/**
 * `null` (or `''`) clears a field. The ISRC is for unreleased rows and manual released rows (a
 * released row of a real catalog track reads it from the catalog); the UPC is for every row.
 */
export type PatchCatalogSongOptions = {
  accountId: number;
  itemUuid: string;
  lyrics?: string | null;
  isrc?: string | null;
  upc?: string | null;
};

/** `POST .../catalog/:itemUuid/released`: a typed draft becomes a released song from a pasted link. */
export type MarkCatalogItemReleasedOptions = {
  accountId: number;
  itemUuid: string;
  /** At least one link. */
  serviceLinks: Record<string, string>;
  /** ISO 8601. */
  releaseDate?: string;
  releaseType?: string;
  artworkUrl?: string;
  isrc?: string;
  upc?: string;
};

/** `GET .../catalog/:itemUuid/lyric-timing` (spec 2026-10-05 §5.9). */
export type LyricTimingResponse = {
  timing: LyricTimingV1 | null;
};

export type GetLyricTimingOptions = {
  accountId: number;
  itemUuid: string;
};

/**
 * `PATCH .../catalog/:itemUuid/lyric-timing`: new starts for 1 to 200 lines. Errors (`cause.code`):
 * 400 `M24_LYRIC_TIMING_ORDER` (with `index`), 409 `M24_LYRIC_TIMING_STALE`, 409 `M24_LYRIC_TIMING_SYNCING`.
 */
export type PatchLyricTimingOptions = {
  accountId: number;
  itemUuid: string;
  revision: number;
  lines: Array<{ index: number; startMs: number | null }>;
};

/**
 * `POST .../catalog/:itemUuid/lyric-timing/resync` → 202 `{ timing }` (`syncing`). Errors: 400
 * `M24_NO_MASTER`, `M24_MASTER_PROCESSING`, `M24_MASTER_FAILED`, `M24_NO_LYRICS`; 429 after 10 an hour.
 */
export type ResyncLyricTimingOptions = {
  accountId: number;
  itemUuid: string;
};

/** `POST .../catalog/:itemUuid/master/stream` and `.../master/download` (design release-kit o). */
export type MasterLinkOptions = {
  accountId: number;
  itemUuid: string;
};

/** A short-lived link to the AAC copy of the master, for the player. */
export type MasterStreamLinkResponse = {
  url: string;
  expiresAt: string;
};

/** A short-lived link to the original master, saved under its own name. */
export type MasterDownloadLinkResponse = {
  url: string;
  name: string;
  sizeBytes: number;
  expiresAt: string;
};

/** `POST .../catalog/:itemUuid/master/uploads`: starts a direct upload of the master to S3 (D29). */
export type CreateMasterUploadOptions = {
  accountId: number;
  itemUuid: string;
  fileName: string;
  contentType: string;
  sizeBytes: number;
  rightsConfirmed?: boolean;
};

/** One part of the file and its presigned `PUT` address. The address is the auth. */
export type MasterUploadPart = {
  partNumber: number;
  url: string;
};

export type CreateMasterUploadResponse = {
  uploadId: string;
  key: string;
  partSizeBytes: number;
  parts: MasterUploadPart[];
  expiresAt: string;
};

export type CompleteMasterUploadOptions = {
  accountId: number;
  itemUuid: string;
  uploadId: string;
  key: string;
  /** The `ETag` of each part, as S3 sent it. */
  parts: Array<{ partNumber: number; etag: string }>;
};

export type AbortMasterUploadOptions = {
  accountId: number;
  itemUuid: string;
  uploadId: string;
  key: string;
};

/** Starts a Composer video upload (`POST accounts/:id/promo/post-media/uploads`). */
export type CreatePostVideoUploadOptions = {
  accountId: number;
  fileName: string;
  contentType: string;
  sizeBytes: number;
};

/** The same shape as a master upload: one presigned `PUT` address per part. */
export type CreatePostVideoUploadResponse = CreateMasterUploadResponse;

/** The measures of the video as the browser read them; the server uses them when ffprobe cannot. */
export type PostVideoMeasures = {
  width: number;
  height: number;
  durationSeconds: number;
};

export type CompletePostVideoUploadOptions = {
  accountId: number;
  uploadId: string;
  key: string;
  parts: Array<{ partNumber: number; etag: string }>;
  measures: PostVideoMeasures | null;
};

/** The saved video: the file the Composer attaches. */
export type SerializedPostVideo = {
  id: number;
  uuid: string;
  url: string;
  mimeType: string;
  sizeBytes: number | null;
  width: number | null;
  height: number | null;
  durationSeconds: number | null;
};

export type CompletePostVideoUploadResponse = { file: SerializedPostVideo };

export type AbortPostVideoUploadOptions = {
  accountId: number;
  uploadId: string;
  key: string;
};

/**
 * A release-day card (design g3). `linked`: the unreleased row is now the released track. `ask`:
 * a released track looks like the row, and the artist confirms it.
 */
export type CatalogReleaseEvent = {
  id: number;
  kind: 'linked' | 'ask';
  catalogItemUuid: string;
  title: string;
  candidate: {
    maid: string;
    title: string;
    artistName: string;
    releaseDate: string | null;
    artworkUrl: string | null;
  };
  /** On a linked event: the services and what moved with the row (design g3). Null on an ask. */
  moved?: {
    serviceCount: number;
    hasMaster: boolean;
    reelCount: number;
    hasPresave: boolean;
  } | null;
};

export type GetCatalogReleaseEventsOptions = {
  accountId: number;
};

export type GetCatalogReleaseEventsResponse = {
  events: CatalogReleaseEvent[];
};

export type DismissCatalogReleaseEventOptions = {
  accountId: number;
  eventId: number;
};

export type LinkCatalogItemOptions = {
  accountId: number;
  itemUuid: string;
  maid: string;
};

export type GetClaimMatchOptions = {
  accountId: number;
  maid: string;
};

/** The unreleased row that the track of a claim looks like (design g3, the blue note). */
export type GetClaimMatchResponse = {
  item: { uuid: string; title: string } | null;
};

export type GetCatalogOptions = {
  accountId: number;
  search?: string;
  /** Left out for `all` — the server takes no filter to mean everything. */
  filter?: Exclude<CatalogFilterValue, CatalogFilterValue.all>;
};

export type GetCatalogResponse = {
  items: CatalogItem[];
};

/**
 * The snapshot the screen copies out of musicapi. It travels with the claim so
 * one tap does not wait on the server fetching what the app already holds.
 */
export type ClaimCatalogItemOptions = {
  accountId: number;
  catalogTrackMaid: string;
  ownership?: CatalogItemOwnership;
  title: string;
  artistName: string;
  releaseType?: string;
  /** ISO 8601. */
  releaseDate?: string;
  artworkUrl?: string;
  serviceLinks?: Record<string, string>;
  /** Links the track to this unreleased row instead of adding a new row (design g3). */
  linkToItemUuid?: string;
  /**
   * The artist chose a new row. Without it the server merges the claim into an unreleased row
   * of the same song (same ISRC or UPC, or the same title and release type).
   */
  keepSeparate?: boolean;
  /** "Merge" on a suggestion (design catalog-merge c1-c3): the release joins this row; no row is added. */
  mergeIntoItemUuid?: string;
  /** "Auto add to my Bio Link" (design smartlink-editor e1). Left out: the Bio Link setting decides. */
  addToBioLink?: boolean;
};

export type CatalogItemResponse = {
  item: CatalogItem;
  /** What the Bio Link auto add did (the add-song routes only; absent on an older API). */
  bioLink?: BioLinkAutoAddResult;
};

/** A row of the same song that a claim found (music24-api `SerializedMergeCandidate`). */
export type CatalogMergeCandidate = {
  uuid: string;
  kind: CatalogItemKind;
  title: string;
  artistName: string;
  releaseType: string | null;
  /** ISO 8601. */
  releaseDate: string | null;
  artworkUrl: string | null;
};

/**
 * The claim answer. `mergedIntoUuid`: the claim joined that row by itself (a sure match), and
 * `item` is that row. `mergeCandidates`: rows that can be the same song (design catalog-merge a4).
 * Both are absent on an older API.
 */
export type CatalogClaimResponse = CatalogItemResponse & {
  mergedIntoUuid?: string;
  mergeCandidates?: CatalogMergeCandidate[];
};

/** One row of the "Merge with ..." picker (design catalog-merge a2). */
export type CatalogMergeOption = CatalogMergeCandidate & {
  /** The same code, or the same title and artist. The picker shows these on top. */
  likelyDuplicate: boolean;
  /** The row that stays when the two rows merge. */
  keepUuid: string;
};

export type GetCatalogMergeCandidatesOptions = { accountId: number; itemUuid: string };

export type GetCatalogMergeCandidatesResponse = { candidates: CatalogMergeOption[] };

export type MergeCatalogItemsOptions = {
  accountId: number;
  /** The row that stays. */
  keepUuid: string;
  /** The row that leaves the catalog. */
  fromItemUuid: string;
};

/**
 * A release that is out on the services but that our catalog cannot find.
 *
 * `catalogTrackMaid` is only sent when the app recognised the release in the
 * catalog after all; without it the server mints a synthetic `manual:` key.
 * The links are typed by the artist, so the server checks each one against the
 * service it is filed under.
 */
export type AddReleasedCatalogItemOptions = {
  accountId: number;
  catalogTrackMaid?: string;
  ownership?: CatalogItemOwnership;
  title: string;
  artistName: string;
  releaseType?: string;
  /** ISO 8601. */
  releaseDate?: string;
  artworkUrl?: string;
  artworkFileId?: number;
  serviceLinks?: Record<string, string>;
  /** The ISRC that a pasted link gave (musicapi inspect). */
  isrc?: string;
  upc?: string;
  /** "Auto add to my Bio Link" (design smartlink-editor e1). Left out: the Bio Link setting decides. */
  addToBioLink?: boolean;
};

export type AddUnreleasedCatalogItemOptions = {
  accountId: number;
  title: string;
  artistName: string;
  releaseType?: string;
  /** ISO 8601. Optional: a date only becomes load-bearing with a campaign. */
  releaseDate?: string;
  artworkFileId?: number;
  /** "Auto add to my Bio Link" (design smartlink-editor e1). Left out: the Bio Link setting decides. */
  addToBioLink?: boolean;
};

// -------------------------------- BIO LINK SETTINGS --------------------------------

export type GetBioLinkSettingsOptions = { accountId: number };

export type GetBioLinkSettingsResponse = BioLinkSettingsResponse;

export type UpdateBioLinkSettingsOptions = { accountId: number } & Partial<BioLinkSettings>;

/**
 * `ownership` is correctable on any row. Everything else is unreleased-only —
 * a claimed row is a snapshot, and retitling it would leave the two
 * disagreeing. `null` clears an optional field.
 */
export type PatchCatalogItemOptions = {
  accountId: number;
  itemUuid: string;
  ownership?: CatalogItemOwnership;
  title?: string;
  artistName?: string;
  releaseType?: string | null;
  releaseDate?: string | null;
  artworkFileId?: number | null;
  /**
   * The whole map, replacing whatever the row held. `null` clears it. A service
   * the artist emptied is an absent key, so a partial send would quietly keep
   * the link it was meant to remove.
   */
  serviceLinks?: Record<string, string> | null;
};

export type DeleteCatalogItemOptions = {
  accountId: number;
  itemUuid: string;
};

export type CatalogSuggestion = {
  /**
   * What claim and dismiss are keyed on. A catalog suggestion carries a real
   * musicapi maid; a profile one carries `profile:<integrationType>:<releaseId>`,
   * which nothing may look up in musicapi.
   */
  catalogTrackMaid: string;
  title: string;
  artistName: string;
  albumName: string | null;
  artworkUrl: string | null;
  /** 0..1, two decimals. Printed as a percentage on catalog rows only — a profile row is always 1. */
  confidence: number;
  source: CatalogSuggestionSource;
  /** The service the profile belongs to. `null` on a catalog suggestion. */
  integrationType: string | null;
  /** Profile suggestions only. */
  releaseType: string | null;
  /** Profile suggestions only, `YYYY-MM-DD`. */
  releaseDate: string | null;
  /** Profile suggestions only: one `{ [integrationType]: url }` entry. */
  serviceLinks: Record<string, string> | null;
  /**
   * The catalog row that this suggestion is the same song as (design catalog-merge c1), or null.
   * Optional: an older server sends no field.
   */
  matchesCatalogItem?: CatalogSuggestionMatch | null;
};

/** music24-api `CatalogSuggestionMatch`: the row of the catalog that a suggestion matches. */
export type CatalogSuggestionMatch = {
  /** The uuid of the row. */
  id: string;
  title: string;
  /** `YYYY-MM-DD`, or null. */
  releaseDate: string | null;
  artworkUrl: string | null;
  confidence: CatalogSuggestionMatchConfidence;
};

export type GetCatalogSuggestionsOptions = {
  accountId: number;
};

export type GetCatalogSuggestionsResponse = {
  suggestions: CatalogSuggestion[];
};

export type DismissCatalogSuggestionOptions = {
  accountId: number;
  catalogTrackMaid: string;
  /** "Not the same" (design catalog-merge c1): keep the suggestion, drop its match. */
  notTheSame?: boolean;
};

export type ServiceConnectionBase = {
  /**
   * A MusicAPIIntegrationID from `catalogConnectionServices`, or a distributor id
   * (`DistributorID`) from `catalogDistributorServices`.
   */
  integrationType: string;
  status: ServiceConnectionStatus;
  artistProfileUrl: string | null;
  /**
   * Whether a daily read of the saved profile url is running.
   *
   * `null` is "there is no url to judge" and says nothing. `false` covers both a
   * link we cannot read and a registration that did not get through, so the copy
   * for it stays soft and offers another save.
   */
  scrapeSupported: boolean | null;
  requestedAt: string | null;
  connectedAt: string | null;
};

/**
 * A row as an older server sends it. The artist-portal fields were added later,
 * so the app must read a row that carries none of them.
 */
export type RawServiceConnection = ServiceConnectionBase & Partial<ArtistPortalConnectionFields>;

/** A row after the slice filled the artist-portal fields. */
export type ServiceConnection = ServiceConnectionBase & ArtistPortalConnectionFields;

export type GetServiceConnectionsOptions = {
  accountId: number;
};

export type GetServiceConnectionsResponse = {
  connections: ServiceConnection[];
};

export type RawGetServiceConnectionsResponse = {
  connections: RawServiceConnection[];
};

export type PatchServiceConnectionOptions = {
  accountId: number;
  integrationType: string;
  /** `null` clears the saved url and drops the card back to not connected. */
  artistProfileUrl: string | null;
};

/** Why the artist asks: these two also send the request to support@music24.com. */
export type ServiceConnectionRequestReason = 'labelHoldsAccess' | 'invitePending';

export type RequestServiceConnectionOptions = {
  accountId: number;
  integrationType: string;
  reason?: ServiceConnectionRequestReason;
};

/** POST /accounts/:accountId/serviceConnections/:integrationType/earlyAccess/request */
export type RequestEarlyAccessOptions = {
  accountId: number;
  integrationType: string;
  /** The email the artist uses on that service. Left out when the field is empty. */
  loginEmail?: string;
};

export type ServiceConnectionResponse = {
  connection: ServiceConnection;
};

export type RawServiceConnectionResponse = {
  connection: RawServiceConnection;
};

/* ----------------------------- Artist portals ----------------------------- */

export type ArtistPortalServiceOptions = {
  accountId: number;
  integrationType: string;
};

export type BeginArtistPortalAuthOptions = ArtistPortalServiceOptions & {
  /** Only an `oauthCallback` service reads it. See `ArtistPortalAuthBeginBody`. */
  returnTo?: string;
};

export type GetArtistPortalArtistsResponse = ArtistPortalArtistsResponse;

export type BeginArtistPortalAuthResponse = ArtistPortalAuthBeginResponse;

export type CompleteArtistPortalAuthOptions = ArtistPortalServiceOptions & {
  /** The oauth redirect url, or the email and password, by auth kind. */
  body: ArtistPortalAuthCompleteBody;
};

export type CompleteArtistPortalAuthResponse = {
  connection: ServiceConnection;
  /** More than one entry means the artist must pick before any sync runs. */
  artists: ArtistPortalArtistRef[];
  /** SoundCloud: where the return page sends the artist (the `returnTo` of `auth/begin`). */
  returnTo?: string;
};

export type SelectArtistPortalArtistOptions = ArtistPortalServiceOptions & {
  externalArtistId: string;
};

/** The window lengths the server accepts for `days`. */
export type ArtistPortalRangeDays = 7 | 28 | 90 | 180 | 365;

/**
 * The window an artist-portal read covers.
 *
 * Portal numbers lag about two days, so a window that ends today is short of
 * its last days while the window before it is full, and every change reads
 * negative. `days` lets the server end the window at the newest day it has
 * synced, which is the shape the app asks for. `from`/`to` stay for a caller
 * that needs exact dates, and the two are mutually exclusive.
 */
export type ArtistPortalRangeOptions =
  | { days: ArtistPortalRangeDays; from?: never; to?: never }
  /** YYYY-MM-DD. Omit all three to let the server pick its own window. */
  | { days?: never; from?: string; to?: string };

export type GetArtistPortalOverviewOptions = {
  accountId: number;
} & ArtistPortalRangeOptions;

export type GetArtistPortalTracksOptions = {
  accountId: number;
  integrationType?: string;
  sort?: string;
  limit?: number;
};

export type GetArtistPortalTrackDailyOptions = {
  accountId: number;
  integrationType: string;
  externalId: string;
} & ArtistPortalRangeOptions;

export type GetArtistPortalPlaylistsOptions = {
  accountId: number;
};

export type GetArtistPortalOverviewResponse = ArtistPortalOverviewResponse;
export type GetArtistPortalTracksResponse = ArtistPortalTracksResponse;
export type GetArtistPortalTrackDailyResponse = ArtistPortalTrackDailyResponse;
export type GetArtistPortalPlaylistsResponse = ArtistPortalPlaylistsResponse;

/** GET accounts/:accountId/streaming/overview?days (design your-intelligence b1). */
export type GetStreamingOverviewOptions = {
  accountId: number;
  days: number;
};

/** GET accounts/:accountId/streaming/tracks?days&limit (design your-intelligence d1). */
export type GetStreamingTracksOptions = {
  accountId: number;
  days: number;
  limit?: number;
};

/** GET accounts/:accountId/artistPortals/ditto/streams/byStore|byCountry (design your-intelligence c). */
export type GetDittoStreamsByDimensionOptions = {
  accountId: number;
  dimension: 'store' | 'country';
  days: number;
  trackExternalId?: string;
};

/** GET accounts/:accountId/streaming/stores|countries: every connected source, merged. */
export type GetStreamingDimensionOptions = {
  accountId: number;
  dimension: 'store' | 'country';
  days: number;
};

/**
 * GET accounts/:accountId/streaming/audience|playSources|shortVideo|accelerator (design
 * streaming-sections a to d). `source` is the `key` of a switch option; absent: the server picks.
 */
export type GetStreamingPeriodSectionOptions = {
  accountId: number;
  days: number;
  source?: string;
};

/** GET accounts/:accountId/streaming/audience: one month, `YYYY-MM`. */
export type GetStreamingAudienceOptions = GetStreamingPeriodSectionOptions & { month?: string };

/** GET accounts/:accountId/streaming/shortVideo: one platform, or all. */
export type GetStreamingShortVideoOptions = GetStreamingPeriodSectionOptions & { platform?: StreamingUgcPlatform };

/** GET accounts/:accountId/streaming/earnings (design your-intelligence e1): every source with earnings. */
export type GetStreamingEarningsOptions = {
  accountId: number;
  groupBy: ArtistPortalEarningsGroup;
  /** YYYY-MM */
  from?: string;
  /** YYYY-MM */
  to?: string;
};

/** One day of the follower series. `followers` is null on a day nothing was read. */
export type ConnectionFollowerPoint = {
  /** A calendar day, `YYYY-MM-DD`. */
  date: string;
  followers: number | null;
};

export type ConnectionInsight = {
  /** The card this belongs to. Match on it, never on array position. */
  integrationType: string;
  status: ArtistScrapeStatus;
  followers: {
    /** `null` on a service that publishes no follower count, which is not zero. */
    latest: number | null;
    /** `null` until a week of history exists, which is not `+0`. */
    weekDelta: number | null;
    /**
     * `latest` minus the last reading on or before `latest` − `periodDays`. `null` when no reading
     * is that old. An old server does not send it.
     */
    periodDelta?: number | null;
    /** The `days` the request asked for. */
    periodDays?: number;
    series: ConnectionFollowerPoint[];
  };
  /** The scraped monthly listeners. Only Spotify sends it, and only when a reading carries it. */
  monthlyListeners?: ConnectionMonthlyListeners | null;
  lastScrapedAt: string | null;
};

export type ConnectionMonthlyListeners = {
  latest: number | null;
  /** Same rule as `followers.periodDelta`. */
  periodDelta: number | null;
  series: { date: string; monthlyListeners: number }[];
};

export type GetConnectionInsightsOptions = {
  accountId: number;
  /** The window of `periodDelta`, 1..90. Left out, the server uses 7. */
  days?: number;
};

export type GetConnectionInsightsResponse = {
  insights: ConnectionInsight[];
};

// ---------------------------- Release kits ----------------------------
// The wire format of music24-api `src/kit/serializeKit.ts`, `ReleaseKits.controller.ts`,
// `src/kit/rollout/buildRollout.ts` and `Credits.controller.ts` (release kit plan, Tasks 5, 11, 13, 15).

export type KitAssetStatus = 'queued' | 'planning' | 'rendering' | 'ready' | 'failed';

/** The statuses of an asset that is still being made. */
export const KIT_ASSET_WORKING_STATUSES: ReadonlyArray<KitAssetStatus> = ['queued', 'planning', 'rendering'];

/**
 * The step of a working asset (the Studio "Working on" strip, design release-kit r2): in the
 * queue, reading the song (a theme that reads the song waits for the song analysis), planning,
 * rendering. Null when the asset is ready or failed.
 */
export type KitAssetStage = 'queued' | 'song' | 'plan' | 'render';

/**
 * The reasons an asset fails. The app maps each code to its own copy. The artwork size is never a
 * fail; an old row can still hold `ARTWORK_TOO_SMALL`, and `kitFailMessage` reads it as a plan failure.
 */
export type KitAssetFailCode =
  | 'RENDER_TIMEOUT'
  | 'ANALYSIS_FAILED'
  | 'SPEC_CHECK_FAILED'
  | 'PLAN_FAILED'
  | 'STALLED'
  | 'RENDER_FAILED'
  | 'LYRICS_NOT_SYNCED'
  | 'BANK_TOO_SMALL'
  // The LiteLLM key of Free and trial workspaces has no budget left this month.
  | 'AI_BUDGET_EXHAUSTED';

/** The step-3 input of the Studio. A copy of `KitAssetParams` in music24-api `src/kit/director/directAsset.ts`. */
export type KitAssetParams = {
  moment?: StudioMoment;
  lyricLine?: string;
  /** Song time of the start of the window (Canvas, artwork in motion). */
  windowStartSec?: number;
  /** A short video: 3, 5, 8, 15 or 30; the motion artwork: 15, 20 or 30. */
  lengthSec?: 3 | 5 | 8 | 15 | 20 | 30;
  shape?: KitShape;
  /** For the countdown stories and videos: the days before the release. */
  daysToRelease?: 7 | 3 | 1;
  /** The style of the asset (`ASSET_TYPE_STYLES`). Absent: the default style of the song's theme. */
  style?: AssetStyleId;
  /** The shape of the Waveform style. */
  waveform?: WaveformShape;
  /**
   * The Instructions of the asset (`assetInstructions.ts`, at most 600 characters). The Studio
   * sends the text of its field. The server stores and returns only an edited text: absent means
   * the default of the asset type, the theme and the style.
   */
  instructions?: string;
  /** A full video: the part of the song (snapped to downbeats, at least 30 s). Absent: the whole song. */
  trim?: KitAssetTrim;
  /** A lyric moment: the first and the last `lyricTiming` line index (both included). */
  lineFrom?: number;
  lineTo?: number;
  /** An added version ("Add yours", spec 2026-10-09 §3.1): the artist linked or uploaded it. */
  added?: boolean;
  /** An uploaded music video: the check that its sound is the song (D4). Absent before the check. */
  soundCheck?: SoundCheck;
  /** A music video made in the editor: the cut that made it. */
  musicVideoCutUuid?: string;
  musicVideoCutNumber?: number;
  /**
   * A Spotify header (spec 2026-10-17 §2.2, fym-shared `spotifyHeader.ts`): the picture. `photo`
   * needs `headerFileId`; `bank` and `ai` need `headerPictureUuid`. Absent: the blurred cover.
   */
  headerSource?: SpotifyHeaderSource;
  headerFileId?: number;
  headerPictureUuid?: string;
  /** A header photo, bank or AI picture: the point (0 to 1) that goes to the middle of the subject box. */
  focusX?: number;
  focusY?: number;
};

/** One version of one asset. The file urls are the relative `files/<uuid>/download` form. */
export type SerializedKitAsset = {
  uuid: string;
  themeId: StudioThemeId;
  variant: string;
  specId: AssetSpecId;
  status: KitAssetStatus;
  /** 0..100 while the asset renders. */
  progress: number;
  /**
   * A full video: the render parts and the parts that are FINISHED; null (or absent, an older API)
   * for every other asset. Show the part in progress with `renderPartInProgress` (fym-shared).
   */
  renderPartsTotal?: number | null;
  renderPartsDone?: number | null;
  /** A full video that is still being made: the time left in seconds; else null. */
  etaSec?: number | null;
  failCode: KitAssetFailCode | null;
  credits: number;
  version: number;
  watermark: boolean;
  /** The Studio input of this version. "New version" opens the Studio with it. */
  params: KitAssetParams;
  /**
   * The release theme of this version (of its look copy). It differs from `kit.look.themeId`
   * when the song changed its theme after the version was made: the version is stale.
   */
  releaseThemeId: ReleaseThemeId;
  /**
   * The five colours of this version (the kit's colours when it was made). It differs from
   * `kit.palette` when the colours changed after the version was made. Null for an old version.
   */
  palette: string[] | null;
  /** The style of this version. Null for an asset type with no styles (the cover sizes). */
  style: AssetStyleId | null;
  /** The shape of the Waveform style, else null. */
  waveform: WaveformShape | null;
  /** The FileModel id of the ready file. "Post it" hands it to the Composer. */
  fileId: number | null;
  fileUrl: string | null;
  posterUrl: string | null;
  /**
   * The imgproxy sources of `fileUrl`, `posterUrl` and `thumbUrl` (music24-api `serializeKitAsset`,
   * since 2026-10-28). Draw a picture through `kitPictureSrc`. Absent in an older answer.
   */
  fileSourceUrl?: string | null;
  posterSourceUrl?: string | null;
  thumbSourceUrl?: string | null;
  /**
   * A small JPEG of the asset (640 × 800, or a 1072 × 603 poster of a full video): the thumbnail of
   * the work emails. Null until it is made; absent in an older answer. The Composer picker tiles use it.
   */
  thumbUrl?: string | null;
  /**
   * Only an old motion artwork row (a ProRes MOV from before 2026-10-07) has it: its H.264 preview.
   * A new motion artwork is an MP4 that plays in the browser, so it is null. Use `motionLoopUrl`.
   */
  previewUrl?: string | null;
  /**
   * True for a preview render of the Free plan or the trial: half size, a low bit rate and the
   * watermark. Absent in an older answer.
   */
  preview?: boolean;
  /**
   * True for an old ready version whose files the server deleted (it keeps the files of the newest
   * 3 versions of each asset). Then `fileUrl`, `posterUrl` and `fileId` are null. Absent in an older answer.
   */
  fileRemoved?: boolean;
  /**
   * A full video: its YouTube description with the SmartLink in the first line. Null for every
   * other asset; absent in an older answer.
   */
  youtubeDescription?: string | null;
  /** The rendered size: a Free short is 720 × 1280. */
  width: number | null;
  height: number | null;
  durationSec: number | null;
  /** The size of the ready file in bytes; null while it works (absent on an older API). */
  sizeBytes?: number | null;
  createdAt: string;
  renderedAt: string | null;
  supersededAt: string | null;
  /** The "Make the kit" click that made this version; null for a single asset. */
  batchId: string | null;
  /** The step while the asset works; null when it is ready or failed. */
  stage: KitAssetStage | null;
  /** The last change of the row (status, progress). */
  updatedAt: string;
  /** When the asset became ready or failed; null while it works. */
  finishedAt: string | null;
  /**
   * Where the version comes from (spec 2026-10-09 §3.1): `made` by Music24, an `upload` or a
   * `youtube` link of the artist. Absent in an older answer: read it with `kitAssetSourceOf`.
   */
  source?: KitAssetSource;
  /** `added` for a version the artist added (an upload or a link), else `made`. Absent in an older answer. */
  origin?: 'made' | 'added';
  /** The YouTube facts of a linked version; null for every other source. */
  youtube?: KitAssetYouTube | null;
  /** An uploaded music video: the sound check (D4). Null before the check and for other sources. */
  soundCheck?: SoundCheck | null;
};

export type SerializedKit = {
  uuid: string;
  catalogItemUuid: string;
  look: ReleaseKitLook;
  /** The five colours that every screen draws with: the override, else the cover colours. */
  palette: string[];
  /** Five hex colours from the song's artwork (the auto colours). */
  coverPalette: string[];
  /** The five colours that the artist set, or null when the kit uses the cover colours. */
  paletteOverride: string[] | null;
  /** The colours of every asset of the song: the palette shaped by the theme (`themeColours`). */
  colours: ThemeColours;
  logoUrl: string | null;
  promoCampaignUuid: string | null;
  /**
   * True when the artist chose the release theme. False, or absent in an older answer, for a kit
   * that only has the default look: the client then treats the theme as not chosen.
   */
  lookChosen?: boolean;
};

/** The month's video credits: the plan count first, then the packs (they never expire). */
export type VideoCreditBalance = {
  /** Plan credits used this month. */
  used: number;
  /** Plan credits a month; null means no limit. */
  limit: number | null;
  /** Pack credits left. */
  packBalance: number;
  /** ISO time of the next plan reset. */
  resetsAt: string;
};

export type GetVideoCreditsOptions = { accountId: number };

/** `GET /accounts/:accountId/credits`. */
export type GetVideoCreditsResponse = VideoCreditBalance & {
  plan: Music24PlanTier;
  canRenderFullSongVideos: boolean;
  /** Only the workspace owner buys packs. */
  isOwner: boolean;
};

/** The credits answer with the sums the screens show. */
export type VideoCredits = GetVideoCreditsResponse & {
  /** Plan credits left this month; null means no limit. */
  planLeft: number | null;
  /** Plan and pack credits left together; null means no limit. */
  totalLeft: number | null;
};

export type ReleaseKitSummary = {
  catalogItemUuid: string;
  /** Null for a song with only reels. */
  kitUuid: string | null;
  /** The look of the kit, or null for a song with no kit (the song picker card, design t1). */
  look: ReleaseKitLook | null;
  /** The 5 cover colours of the kit; empty for a song with no kit. */
  palette: string[];
  readyCount: number;
  totalCount: number;
  workingCount: number;
  /** The current versions that failed plus the newest reel when it failed (navigation badge r7a). */
  failedCount: number;
  /** The newest of those failures; null when nothing failed. */
  lastFailedAt: string | null;
  updatedAt: string;
  /** With `include: 'assets'` only: the current ready versions. */
  assets?: SerializedKitAsset[];
  /** With `include: 'assets'` only: the ready reels. */
  reels?: SerializedReel[];
  /** With `include: 'assets'` only: the current versions that failed. */
  failedAssets?: SerializedKitAsset[];
  /** With `include: 'assets'` only: the newest reel of the song when it failed. */
  failedReels?: SerializedReel[];
};

export type GetReleaseKitsOptions = { accountId: number; include?: 'assets' };

/**
 * `GET /accounts/:id/work/summary?scope=assets` (music24-api `src/kit/work/workTypes.ts`): the
 * kit assets and AI reels that are queued or being made, and their open failures (failed in the
 * last 7 days and not tried again).
 */
export type WorkSummary = { working: number; failed: number; lastFailedAt: string | null };

export type GetReleaseKitsResponse = { kits: ReleaseKitSummary[] };

export type GetReleaseKitOptions = { accountId: number; catalogItemUuid: string };
export type GetReleaseKitResponse = {
  /** Null when nothing was made for the song yet. */
  kit: SerializedKit | null;
  /**
   * The facts of the Story preview that the song row does not hold: the published SmartLink
   * address of the song with no scheme (null: none) and the release date (`YYYY-MM-DD`). The render
   * reads the same values. `presave`: the song has a published pre-save page (a feed post says
   * "Coming soon" without one; absent on an older answer: true). Absent on an older answer.
   */
  /** `locale`: the language of the kit words (the setting of the workspace owner); missing from an older server. */
  story?: { link: string | null; releaseDate: string | null; presave?: boolean; locale?: string };
  /** The current version of each theme and variant. */
  assets: SerializedKitAsset[];
  /**
   * Releases v2: the newest revision of each theme and variant, also a failed or working one while
   * an older ready revision is current. Absent on an older answer: read `assets`.
   */
  newest?: SerializedKitAsset[];
  /**
   * The newest ready revision of each theme and variant, also while a newer revision is still
   * made. The Composer media picker lists these. Absent on an older answer: read `assets`.
   */
  ready?: SerializedKitAsset[];
  /** The reels of the song, newest first, at most 20. */
  reels: SerializedReel[];
  credits: VideoCreditBalance;
};

export type CreateKitAssetItem = { themeId: StudioThemeId; params: KitAssetParams };

export type CreateKitAssetsOptions = {
  accountId: number;
  catalogItemUuid: string;
  /** 1 to 20 items. `aiReel` is not accepted: use `createReel`. */
  items: CreateKitAssetItem[];
  /** True for one click that makes many assets ("Make the kit"): one ready email for the batch. */
  batch?: boolean;
};

export type CreateKitAssetsResult = {
  kitUuid: string;
  created: SerializedKitAsset[];
  /** Themes skipped because the same theme and variant is still being made. */
  busy: Array<{ themeId: StudioThemeId; variant: string }>;
  /**
   * Themes left out with no charge because they read the song and it has no ready master
   * (`needsMaster`) or its master is still processing (`masterProcessing`). The others are made.
   */
  skipped?: Array<{ themeId: StudioThemeId; reason: 'needsMaster' | 'masterProcessing' }>;
};

/**
 * The public share link of one made asset (`/accounts/:accountId/asset-shares`, design
 * music24-asset-viewer a1..a5). The shapes live in fym-shared `assetShares.ts`.
 */
export type {
  AssetShareAccess,
  AssetShareAccessResponse,
  AssetShareKind,
  AssetShareResponse,
  PublicAssetShare,
  SerializedAssetShare,
};

export type AssetShareOptions = {
  accountId: number;
  kind: AssetShareKind;
  assetUuid: string;
};

export type GetKitAssetVersionsOptions = { accountId: number; catalogItemUuid: string; assetUuid: string };
/** One revision of the history (Releases v2, design d1): the asset and its maker (null for an old row). */
export type SerializedKitAssetVersion = SerializedKitAsset & { createdBy?: { id: number; email: string } | null };
export type GetKitAssetVersionsResponse = { versions: SerializedKitAssetVersion[] };

export type UpdateKitLookOptions = {
  accountId: number;
  catalogItemUuid: string;
  themeId?: ReleaseThemeId;
  /** Null: the display font of the theme. */
  fontOverride?: KitFontId | null;
  logoFileId?: number | null;
  /** Five `#rrggbb` colours; null goes back to the cover colours. */
  paletteOverride?: string[] | null;
  /** Also write the look as the artist's default for new kits. */
  saveAsDefault?: boolean;
};
export type UpdateKitLookResponse = { kit: SerializedKit };

/** "Make them again" (`POST .../assets/remake`): new versions of current assets in the song's theme. */
export type RemakeKitAssetsOptions = {
  accountId: number;
  catalogItemUuid: string;
  /** 1 to 50 current assets of the song. */
  assetUuids: string[];
};

/** "Redo the kit with the new cover" (fym-shared `music24/coverRedo.ts`): the song of the kit page. */
export type CoverRedoOptions = { accountId: number; catalogItemUuid: string };

/** The dry run and the start of a cover redo. */
export type StartCoverRedoOptions = CoverRedoOptions & CoverRedoRequest;

/** "Redo the kit with the new release date" (fym-shared `music24/dateRedo.ts`): the song of the kit page. */
export type DateRedoOptions = { accountId: number; catalogItemUuid: string };

/** The dry run and the start of a date redo. */
export type StartDateRedoOptions = DateRedoOptions & DateRedoRequest;

/** The answer of the dry run: the cost, and what each asset becomes. Nothing is charged. */
export type RemakeKitAssetsPreview = {
  credits: number;
  items: Array<{
    assetUuid: string;
    themeId: StudioThemeId;
    variant: string;
    credits: number;
    fromReleaseThemeId: ReleaseThemeId;
    toReleaseThemeId: ReleaseThemeId;
  }>;
};

export type ReleaseKitLookOptions = { accountId: number };
export type GetReleaseKitLookResponse = {
  look: ReleaseKitLook | null;
  savedFrom: { catalogItemUuid: string; title: string; at: string } | null;
};

/** The artist time zone (Settings → Artist → Time zone). null = never set. */
export type AccountTimeZoneOptions = { accountId: number };
export type AccountTimeZoneResponse = { timeZone: string | null };
export type UpdateAccountTimeZoneOptions = { accountId: number; timeZone: string };

export type CreateKitZipOptions = { accountId: number; catalogItemUuid: string };
/**
 * The url when the zip is ready within 20 s; else the server emails the link. `requestId` names
 * the request for `getKitZip` (an older server sends none). `fileName` is the name the link saves
 * the zip under ("LaziiBee - Forests - Release kit.zip"); an older server sends none.
 */
export type CreateKitZipResponse = { url: string; fileName?: string } | { status: 'emailing'; requestId?: string };

export type GetKitZipOptions = { accountId: number; catalogItemUuid: string; requestId: string };
/** The zip of a request that went to the email. The `kit.zip.updated` event says when to read it. */
export type GetKitZipResponse =
  | { status: 'working' }
  | { status: 'ready'; url: string; fileName?: string }
  | { status: 'failed' };

/** The ProRes file of a motion artwork file (design w): `GET` / `POST .../assets/:assetUuid/prores`. */
export type KitProResOptions = { accountId: number; catalogItemUuid: string; assetUuid: string };
export type KitProResResponse =
  | { status: 'none' }
  | { status: 'working' }
  | { status: 'failed' }
  | { status: 'ready'; url: string; fileName: string; expiresAt: string; sizeBytes: number | null };

export type RolloutPreviewDay = {
  key: RolloutDayKey;
  /** YYYY-MM-DD: the real day of the step (fym-shared `scheduleRolloutSteps`). */
  date: string;
  /** The real offset of `date` from the release day (or today for a push). Absent in an older answer. */
  offsetDays?: number;
  /** The offset of the rollout table. Absent in an older answer. */
  plannedOffsetDays?: number;
  /** The planned day had passed, so the step moved forward. */
  movedToToday: boolean;
  /** A build-up step of a release that passed: it keeps its past day. */
  missed?: boolean;
  isTask: boolean;
  format: 'post' | 'story' | 'reel';
  asset: {
    themeId: StudioThemeId;
    variant: string;
    status: 'ready' | 'working' | 'missing';
    assetUuid: string | null;
  } | null;
  /** The selected channels that take this format. */
  platforms: SocialPlatform[];
  /**
   * The drafts of this day exist already: the song's campaign has a post with this day's
   * `rolloutKey` (design m3b, "Already on your calendar"). A click adds no second set.
   */
  added: boolean;
};

export type GetRolloutOptions = {
  accountId: number;
  catalogItemUuid: string;
  kind: RolloutKind;
  platforms: SocialPlatform[];
};
export type GetRolloutResponse = {
  days: RolloutPreviewDay[];
  /** The drafts one click adds now: the days with a ready asset and no drafts yet, per channel. */
  postCount: number;
  channelCount: number;
  campaignUuid: string | null;
};

export type AddRolloutDraftsOptions = GetRolloutOptions & {
  /** Releases v2: only these days (one gap, or the ticked push weeks). */
  dayKeys?: RolloutDayKey[];
  /** Releases v2: a missing kit asset of a day becomes a Studio job first (`assetQueued`). */
  makeMissing?: boolean;
  /** Design rollout-plan (decision 3): a day whose asset is being made gets its drafts when it is ready. */
  autoDraft?: boolean;
};
export type AddRolloutDraftsResponse = {
  campaignUuid: string;
  created: number;
  skipped: Array<{ key: RolloutDayKey; reason: 'assetMissing' | 'alreadyAdded' | 'assetQueued' | 'skipped' }>;
  /** With `makeMissing`: the kit assets queued for the days. Absent on an older answer. */
  queued?: Array<{ key: RolloutDayKey; themeId: StudioThemeId; variant: string }>;
  /** With `autoDraft`: the days whose drafts come when their asset is ready. */
  autoDraftKeys?: RolloutDayKey[];
};

// ---------------------------- Releases v2 (design music24-releases-v2) ----------------------------
export type {
  GetReleasesResponse,
  MarkReleaseViewedResponse,
  ReleaseListRow,
  ReleaseNextStep,
  ReleaseOverview,
  ReleasePlan,
  ReleasePlanChannel,
  ReleasePlanDay,
  ReleasePost,
  ReleaseSetup,
  ReleaseSetupItem,
} from 'fym-shared/src/music24/releases';
export type GetReleasesOptions = { accountId: number };
export type ReleaseOptions = { accountId: number; catalogItemUuid: string };
export type GetReleasePlanOptions = ReleaseOptions & {
  kind: RolloutKind;
  platforms: SocialPlatform[];
  /** The browser time zone: stored as the artist's time zone when the account has none (decision 7). */
  timeZone?: string;
};

/** One step action of the Plan tab (design rollout-plan c1, c2, c3). */
export type PlanStepOptions = ReleaseOptions & {
  kind: RolloutKind;
  dayKey: RolloutDayKey;
  /** The usable channels of the account. */
  platforms: SocialPlatform[];
};
export type SetPlanChannelsOptions = ReleaseOptions & {
  kind: RolloutKind;
  platforms: SocialPlatform[];
  channels: SocialPlatform[];
};
export type SetPlanStepChannelsOptions = PlanStepOptions & { channels: SocialPlatform[] };
export type SetPlanStepChannelsResponse = {
  drafts: AddRolloutDraftsResponse;
  retired: { canceled: number; deleted: number };
};
export type SkipPlanStepResponse = { canceled: number; deleted: number; skippedKeys: string[] };
export type SwapPlanStepMediaOptions = PlanStepOptions & { fileId: number };

// ---------------------------- AI reels plan (design rollout-plan f1-f6, spec 2026-10-12) ----------------------------
export type {
  ReelsPlanCadence,
  ReelsPlanEnded,
  ReelsPlanMade,
  ReelsPlanPostsPerDay,
  ReelsPlanPreview,
  ReelsPlanRefusal,
  ReelsPlanSettings,
  ReleaseReelsPlan,
} from 'fym-shared/src/music24/reelsPlan';
/** The settings of an AI reels plan and its channels (`POST .../release-kits/:uuid/reels-plan`). */
export type ReelsPlanOptions = ReleaseOptions &
  ReelsPlanSettings & {
    /** The usable channels of the plan. The server keeps the channels that take a reel. */
    platforms: SocialPlatform[];
  };

// ---------------------------- Cover editor (spec 2026-10-08 cover editor §5) ----------------------------

/** One saved version of a cover design of a song (music24-api `CoverDesignModel`). */
export type CoverDesign = {
  uuid: string;
  name: string | null;
  /** Null when the stored document does not parse (a document of a later version). */
  doc: CoverDoc | null;
  /** When this version became the cover of the song; null when it never did. */
  appliedAt: string | null;
  createdAt: string;
  updatedAt: string;
};

export type CoverAiPictureStatus = 'queued' | 'working' | 'ready' | 'failed';
export type CoverAiPictureFailCode = 'IMAGE_REFUSED' | 'IMAGE_FAILED' | 'BUDGET_EXCEEDED' | 'STALLED';

/** One AI picture of a run of 4 (music24-api `CoverAiPictureModel`). The urls are null until it is ready. */
export type CoverAiPicture = {
  uuid: string;
  runUuid: string;
  /** 0..3: the place of the picture in its run. */
  index: number;
  status: CoverAiPictureStatus;
  failCode: CoverAiPictureFailCode | null;
  url: string | null;
  thumbUrl: string | null;
  palette: string[];
  instructions: string | null;
  /** True for a picture of the welcome run (on us). Absent in an older answer. */
  welcome?: boolean;
  createdAt: string;
};

/** The AI pictures of the workspace this month. `limit` 0: the plan has no AI. */
export type CoverAiQuota = { used: number; limit: number; resetsAt: string };

/**
 * The welcome run of the workspace (owner request 2026-10-09): the first AI run of a trial
 * workspace is on us, once per workspace. `available` is false on every other plan and after it.
 */
export type CoverWelcomeRun = { available: boolean; pictures: number };

export type CoverEditorSong = {
  title: string;
  artistName: string;
  kind: CatalogItemKind;
  themeId: ReleaseThemeId;
  palette: string[];
  artworkUrl: string | null;
  /**
   * The version that is the cover of the song now ("Use as cover" took it and the song still has
   * that file); null for an uploaded cover, a store cover or no cover. Absent in an older answer.
   */
  coverDesignUuid?: string | null;
};

export type GetCoverEditorOptions = { accountId: number; catalogItemUuid: string };

export type GetCoverEditorResponse = {
  song: CoverEditorSong;
  designs: CoverDesign[];
  /** The newest AI pictures of the song: the newest run first, each run in its own order. */
  pictures: CoverAiPicture[];
  quota: CoverAiQuota;
  /** The welcome run of the workspace. Absent in an older answer: no welcome run. */
  welcomeRun?: CoverWelcomeRun;
  plan: Music24PlanTier;
  /** True when the download of this workspace carries the Music24 watermark (Free, trial). */
  watermark: boolean;
  /** True when the artist confirmed the rights to the reference pictures. */
  rightsConfirmed: boolean;
};

export type CreateCoverDesignOptions = GetCoverEditorOptions & { doc: CoverDoc; name?: string };
export type UpdateCoverDesignOptions = {
  accountId: number;
  designUuid: string;
  doc?: CoverDoc;
  name?: string;
};
export type DeleteCoverDesignOptions = GetCoverEditorOptions & { designUuid: string };
export type CoverDesignResponse = { design: CoverDesign };

export type CreateCoverAiRunOptions = GetCoverEditorOptions & {
  instructions?: string;
  referenceFileIds?: number[];
  referenceRightsConfirmed?: boolean;
};
export type CreateCoverAiRunResponse = {
  runUuid: string;
  pictures: CoverAiPicture[];
  quota: CoverAiQuota;
  /** True when this run is the welcome run (on us). Absent in an older answer. */
  welcome?: boolean;
  welcomeRun?: CoverWelcomeRun;
};

export type ApplyCoverDesignOptions = GetCoverEditorOptions & { designUuid: string; fileId: number };
export type ApplyCoverDesignResponse = { item: CatalogItem };

/** One published picture of the cover bank (`GET /public/cover-backgrounds`). */
export type CoverBankBackground = {
  uuid: string;
  url: string;
  thumbUrl: string;
  themeIds: ReleaseThemeId[];
  moods: string[];
  palette: string[];
};
export type GetCoverBackgroundsResponse = { backgrounds: CoverBankBackground[] };

/** The email and the design of a visitor of the public cover maker (`POST /public/cover-maker/leads`, D12). */
export type CreateCoverMakerLeadOptions = {
  email: string;
  doc: CoverDoc;
  title?: string;
  artist?: string;
  /** A JPEG of the design as a base64 data string: at most 800 px and 300 KB. */
  thumbnail?: string;
  locale?: string;
  trackingConsent?: boolean;
  /** The token that this browser got from an earlier download: the API updates that saved design. */
  leadToken?: string;
};
export type CreateCoverMakerLeadResponse = { token: string };
/** The saved design of a lead (`GET /public/cover-maker/leads/:token`). The client parses `doc`. */
export type GetCoverMakerLeadResponse = { doc: unknown; title?: string; artist?: string };

/** The subscriber numbers of the Fans screen (`GET /accounts/:accountId/fans/summary`). */
export type GetFansSummaryOptions = { accountId: number; days: number };
/** One page of the releases that went to the subscribers (`GET /accounts/:accountId/fans/releases`). */
export type GetFansReleasesOptions = { accountId: number; cursor: string | null };

/**
 * `GET /accounts/:accountId/clean-copies` (trial upsell spec 2026-10-13 §5.3): the clean-copies run
 * of the workspace of the account. Null when the licence has no run, and for the demo artist.
 */
export type GetCleanCopiesOptions = { accountId: number };
export type GetCleanCopiesResponse = { cleanCopies: CleanCopiesAnswer | null };

// ---------------------------- Artist Profile (Studio → Profile) ----------------------------
// The answer shapes are in fym-shared `music24/artistProfile.ts`. These are the request options.

/** `GET /accounts/:accountId/artist-profile` and the other calls of one artist. */
export type ArtistProfileOptions = { accountId: number };
/** `POST .../artist-profile/assets { items }`. */
export type MakeProfileAssetsOptions = { accountId: number; items: ProfileMakeItem[] };
/** `POST .../artist-profile/from-release { catalogItemUuid, kitAssetUuid? }`. */
export type UseReleaseForProfileOptions = { accountId: number; catalogItemUuid: string; kitAssetUuid?: string };
/** `GET .../artist-profile/assets/:uuid/versions`, `POST .../assets/:uuid/current`. */
export type ProfileAssetOptions = { accountId: number; assetUuid: string };
export type ProfileAssetVersionsResponse = { versions: SerializedProfileAsset[] };
/** `POST .../assets/:uuid/current` and `.../mark` answer the changed version. */
export type ProfileAssetResponse = { asset: SerializedProfileAsset };
/** `POST .../artist-profile/assets/:uuid/mark { set }`: the manual mark "I set it" of a banner. */
/**
 * `POST .../artist-profile/assets/:assetUuid/mark { set, service? }`: a banner mark, or with `service`
 * the mark of a manual service ("Set these by hand") on the artist photo.
 */
export type MarkProfileAssetOptions = {
  accountId: number;
  assetUuid: string;
  set: boolean;
  service?: ProfileManualService;
};
/** `PUT .../artist-profile/bio { bioShort, bioLong }`. */
export type SaveArtistBioOptions = { accountId: number; bioShort: string; bioLong: string };
export type SaveArtistBioResponse = { bio: ArtistProfileBio };
/** `POST .../artist-profile/bio/draft { facts? }`. */
export type DraftArtistBioOptions = { accountId: number; facts?: string };
/** `POST .../artist-profile/spotify { images, acceptRights? }`. */
export type UploadProfileToSpotifyOptions = {
  accountId: number;
  images: SpotifyProfileImage[];
  acceptRights?: boolean;
};
/** `POST .../artist-profile/spotify/bio { acceptRights? }`. */
export type UploadBioToSpotifyOptions = { accountId: number; acceptRights?: boolean };
/** `GET .../artist-profile/send/:sendUuid`. */
export type ProfileSendOptions = { accountId: number; sendUuid: string };
/** `POST .../artist-profile/send { targets, rightsConfirmed? }` (Profile "Send to all"). */
export type StartProfileSendOptions = {
  accountId: number;
  targets: ProfileSendTarget[];
  rightsConfirmed?: boolean;
};
/** `PUT .../artist-profile/send/settings { keepInSync }`. */
export type SaveProfileSendSettingsOptions = { accountId: number; keepInSync: boolean };
